import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmt, fmtDate } from "../../lib/api";
import { Field, btnGhost, btnPrimary, inputCls } from "../../components/ui";
import { RideDemoBanner, RideStatus, Stars, VEHICLE } from "../../lib/ride";

function DemoToggle({ d, setD }) {
  const toggle = async () => {
    try { const { data } = await api.put("/ride-demo/admin/demo", { enabled: !d.enabled }); setD(data); toast.success(data.enabled ? "Modo DEMO Ride ativado" : "Modo DEMO Ride desativado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="bo-card flex flex-col gap-2 p-5 text-sm" data-testid="ride-demo-toggle">
      <h3 className="font-bold">OBÔGO Ride — modo demonstração</h3>
      {d.enabled && <RideDemoBanner testid="admin-ride-demo-banner" />}
      <p className="text-xs text-gray-500">Transporte real (transport.enabled): <strong data-testid="ride-transport-enabled">{d.transport_enabled ? "ativo" : "desativado"}</strong>. O modo DEMO é independente, usa apenas motoristas, tarifas e viagens simuladas e só está disponível em desenvolvimento.</p>
      {d.allowed ? <button onClick={toggle} className={`${d.enabled ? btnGhost : btnPrimary} w-fit px-4 py-1.5`} data-testid="ride-demo-toggle-btn">{d.enabled ? "Desativar modo DEMO" : "Ativar modo DEMO"}</button>
        : <p className="text-xs font-semibold text-red-600" data-testid="ride-demo-not-allowed">Indisponível fora do ambiente de desenvolvimento.</p>}
    </section>
  );
}

function Fares() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/ride-demo/admin/fares").then(({ data }) => setF(data)); }, []);
  if (!f) return null;
  const set = (v, k, val) => setF({ ...f, [v]: { ...f[v], [k]: Number(val) } });
  const save = async () => { try { setF((await api.put("/ride-demo/admin/fares", f)).data); toast.success("Tarifas DEMO guardadas"); } catch (e) { toast.error(errMsg(e)); } };
  const LABELS = { base: "Base", per_km: "Por km", per_min: "Por min", minimum: "Mínimo", speed_kmh: "Velocidade km/h" };
  return (
    <section className="bo-card flex flex-col gap-3 p-5 text-sm" data-testid="ride-fares-editor">
      <h3 className="font-bold">Tarifas DEMO (STN)</h3>
      {["moto", "carro"].map((v) => (
        <div key={v} className="grid grid-cols-2 gap-2 sm:grid-cols-6"><strong className="self-center">{VEHICLE[v]}</strong>
          {Object.keys(LABELS).map((k) => <Field key={k} label={LABELS[k]}><input type="number" min="0" value={f[v][k]} onChange={(e) => set(v, k, e.target.value)} className={inputCls} data-testid={`ride-fare-${v}-${k}`} /></Field>)}
        </div>
      ))}
      <button onClick={save} className={`${btnPrimary} w-fit px-4 py-1.5`} data-testid="ride-fares-save">Guardar tarifas</button>
    </section>
  );
}

function Drivers() {
  const empty = { name: "", phone: "", vehicle: "moto", plate: "", zone: "", user_email: "" };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const load = () => api.get("/ride-demo/admin/drivers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/ride-demo/admin/drivers", { ...form, user_email: form.user_email || null }); setForm(empty); load(); toast.success("Motorista DEMO criado"); } catch (err) { toast.error(errMsg(err)); }
  };
  const toggle = async (id) => { try { await api.post(`/ride-demo/admin/drivers/${id}/toggle`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const inp = (k, ph) => <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className={inputCls} data-testid={`ride-driver-${k}-input`} />;
  return (
    <section className="bo-card flex flex-col gap-3 p-5 text-sm" data-testid="ride-drivers">
      <h3 className="font-bold">Motoristas DEMO</h3>
      <form onSubmit={create} className="grid gap-2 sm:grid-cols-3">
        {inp("name", "Nome")}{inp("phone", "Telefone")}{inp("plate", "Matrícula")}{inp("zone", "Zona")}{inp("user_email", "Conta associada (email, opcional)")}
        <select value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} className={inputCls} data-testid="ride-driver-vehicle-select"><option value="moto">Mototáxi</option><option value="carro">Táxi</option></select>
        <button className={`${btnPrimary} w-fit px-4 py-1.5`} data-testid="ride-driver-create-btn">Criar motorista DEMO</button>
      </form>
      {items.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center gap-3 border-t border-gray-50 py-2" data-testid={`ride-driver-row-${d.id}`}>
          <strong className="flex-1">{d.name}</strong><span>{VEHICLE[d.vehicle]} {d.plate}</span><span className="text-xs text-gray-500">{d.user_email || "sem conta"}</span>
          {d.rating_avg && <Stars value={Math.round(d.rating_avg)} />}
          <span className={d.active ? "text-bo-green" : "text-red-600"}>{d.active ? (d.available ? "ativo · disponível" : "ativo · indisponível") : "suspenso"}</span>
          <button onClick={() => toggle(d.id)} className={`${btnGhost} px-3 py-1`} data-testid={`ride-driver-toggle-${d.id}`}>{d.active ? "Suspender" : "Reativar"}</button>
        </div>
      ))}
    </section>
  );
}

const SIM = { accepted: ["arrive", "Simular chegada"], arriving: ["start", "Simular início"], in_progress: ["complete", "Simular conclusão"] };

function Trips() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/ride-demo/admin/trips").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const act = async (id, action) => { try { await api.post(`/ride-demo/admin/trips/${id}/advance`, { action }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="bo-card flex flex-col gap-2 p-5 text-sm" data-testid="ride-admin-trips">
      <h3 className="font-bold">Viagens DEMO</h3>
      {!items.length && <p className="text-gray-500">Sem viagens DEMO.</p>}
      {items.map((t) => (
        <div key={t.id} className="flex flex-wrap items-center gap-2 border-t border-gray-50 py-2" data-testid={`ride-admin-trip-${t.number}`}>
          <strong>{t.number}</strong><RideStatus status={t.status} />
          <span className="text-xs text-gray-500">{t.customer_name} · {VEHICLE[t.estimate.vehicle]} · {fmt(t.estimate.price)} · {fmtDate(t.created_at)}{t.driver && ` · ${t.driver.name}`}</span>
          {t.rating && <Stars value={t.rating.stars} />}
          <span className="ml-auto flex gap-2">
            {t.status === "requested" && <button onClick={() => act(t.id, "auto_accept")} className={`${btnPrimary} px-3 py-1`} data-testid={`ride-admin-assign-${t.number}`}>Atribuir motorista</button>}
            {t.driver && !t.driver.user_id && SIM[t.status] && <button onClick={() => act(t.id, SIM[t.status][0])} className={`${btnPrimary} px-3 py-1`} data-testid={`ride-admin-${SIM[t.status][0]}-${t.number}`}>{SIM[t.status][1]}</button>}
            {["requested", "accepted", "arriving"].includes(t.status) && <button onClick={() => act(t.id, "cancel")} className={`${btnGhost} px-3 py-1`} data-testid={`ride-admin-cancel-${t.number}`}>Cancelar</button>}
          </span>
        </div>
      ))}
    </section>
  );
}

export default function AdminRideDemo() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/ride-demo/admin/demo").then(({ data }) => setD(data)); }, []);
  if (!d) return null;
  return (
    <div className="flex flex-col gap-4" data-testid="admin-ride-demo">
      <DemoToggle d={d} setD={setD} />
      {d.enabled && <><Fares /><Drivers /><Trips /></>}
    </div>
  );
}
